import { motion } from "framer-motion";
import { MapPin, Clock, Phone, ArrowUpRight } from "lucide-react";
import { clinicConfig } from "@/config/clinic";
import { useOpenStatus } from "@/hooks/useOpenStatus";
import { fadeUp, viewport } from "@/lib/reveal";

const SCHEDULE = [
  { day: "Monday", open: true },
  { day: "Tuesday", open: true },
  { day: "Wednesday", open: true },
  { day: "Thursday", open: true },
  { day: "Friday", open: false },
  { day: "Saturday", open: false },
  { day: "Sunday", open: false },
];

const MAP_EMBED =
  "https://www.google.com/maps?q=135+W+Main+Street+Keystone+WV+24868&output=embed";

export const VisitInfo = () => {
  const status = useOpenStatus();
  return (
    <section id="visit" data-testid="visit-section" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewport} className="max-w-2xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-accent">Visit the office</p>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-foreground md:text-5xl">
            Where to find us, and when we're open.
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Hours card */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            data-testid="hours-card"
            className="flex flex-col border border-border bg-card p-8 lg:col-span-5"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-primary dark:text-accent" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl font-medium text-foreground">Office Hours</h3>
              </div>
              <span
                data-testid="open-status-badge"
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${
                  status.isOpen
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                    : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${status.isOpen ? "bg-emerald-500" : "bg-amber-500"}`} />
                {status.label}
              </span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{status.detail} · Eastern Time</p>

            <ul className="mt-6 divide-y divide-border">
              {SCHEDULE.map((s) => (
                <li key={s.day} className="flex items-center justify-between py-3 text-base">
                  <span className={s.open ? "font-medium text-foreground" : "text-muted-foreground"}>{s.day}</span>
                  <span className={s.open ? "font-medium text-foreground" : "text-muted-foreground"}>
                    {s.open ? "9:00 AM – 5:00 PM" : "Closed"}
                  </span>
                </li>
              ))}
            </ul>

            <a
              href={`tel:${clinicConfig.phoneTel}`}
              data-testid="visit-call-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3 text-base font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} /> {clinicConfig.phoneDisplay}
            </a>
          </motion.div>

          {/* Location + map */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            data-testid="location-card"
            className="flex flex-col overflow-hidden border border-border bg-card lg:col-span-7"
          >
            <div className="p-8">
              <div className="flex items-center gap-3">
                <MapPin className="h-6 w-6 text-primary dark:text-accent" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl font-medium text-foreground">Our Location</h3>
              </div>
              <p className="mt-4 font-serif text-2xl leading-snug text-foreground">
                {clinicConfig.addressLine1}
                <br />
                {clinicConfig.addressLine2}
              </p>
              <a
                href={clinicConfig.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-map-cta"
                className="mt-5 inline-flex items-center gap-2 text-base font-semibold text-primary underline decoration-accent decoration-2 underline-offset-4 dark:text-accent"
              >
                Get directions
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </a>
            </div>
            <div className="mt-auto h-64 w-full border-t border-border md:h-72">
              <iframe
                title="Map to West Virginia Family Medicine"
                src={MAP_EMBED}
                className="h-full w-full grayscale-[0.2]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VisitInfo;
